import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  CircleX,
  Download,
  KeyRound,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { CreateUserInput, UpdateUserInput, UserListItem } from '@pnl/shared';
import { api } from '../lib/api';
import { useAuthStore } from '../auth/auth.store';

const STATUS_TONE: Record<UserListItem['status'], 'success' | 'neutral' | 'danger' | 'warning'> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  BLOCKED: 'danger',
  PENDING_VERIFICATION: 'warning',
};
const STATUS_LABEL: Record<UserListItem['status'], string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
  PENDING_VERIFICATION: 'Pendente',
};

export function UsersPage() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [creating, setCreating] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['users', { q, page }],
    queryFn: () => api.listUsers({ q: q || undefined, page, pageSize: 20 } as never),
  });

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.listRoles(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const resetPwd = useMutation({
    mutationFn: (v: { id: string; newPassword: string }) =>
      api.resetUserPassword(v.id, { newPassword: v.newPassword }),
    onSuccess: () => alert('Senha redefinida. As sessões do usuário foram encerradas.'),
    onError: (err) =>
      alert(err instanceof PnlApiError ? err.api.message : 'Falha ao redefinir senha.'),
  });

  const [exporting, setExporting] = useState(false);
  async function handleExportCsv(): Promise<void> {
    setExporting(true);
    try {
      const baseUrl = import.meta.env['VITE_API_URL'] ?? 'http://localhost:4000';
      const token = localStorage.getItem('pnl_admin_access');
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const res = await fetch(`${baseUrl}/admin/users/export.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export falhou (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Falha ao exportar CSV:', err);
      alert('Falha ao exportar CSV. Veja o console.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">M1.10</p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Usuários &amp; RBAC
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Gerencie admins, atribua roles e revise últimos acessos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            leadingIcon={<Download size={14} />}
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? 'Exportando…' : 'Exportar CSV'}
          </Button>
          <Button leadingIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
            Novo usuário
          </Button>
        </div>
      </div>

      <Card className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="max-w-md flex-1">
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por email ou nome…"
              leadingIcon={<Search size={14} />}
            />
          </div>
          <span className="text-ink-500 text-xs">
            {usersQuery.data ? `${usersQuery.data.total} resultado(s)` : '—'}
          </span>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {usersQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : usersQuery.data && usersQuery.data.items.length > 0 ? (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Usuário</TH>
                  <TH>Status</TH>
                  <TH>Roles</TH>
                  <TH>2FA</TH>
                  <TH>Verificado</TH>
                  <TH className="text-right">Último login</TH>
                  <TH className="text-right">Ações</TH>
                </TR>
              </THead>
              <TBody>
                {usersQuery.data.items.map((u) => (
                  <TR key={u.id}>
                    <TD>
                      <div className="flex items-center gap-3">
                        <div className="bg-brand-100 text-2xs text-brand-700 flex h-9 w-9 items-center justify-center rounded-full font-mono font-semibold">
                          {(u.fullName || u.email)
                            .split(' ')
                            .map((s) => s[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <div>
                          <p className="text-ink-900 font-medium">{u.fullName || '—'}</p>
                          <p className="text-ink-500 text-xs">{u.email}</p>
                        </div>
                      </div>
                    </TD>
                    <TD>
                      <Badge tone={STATUS_TONE[u.status]} dot>
                        {STATUS_LABEL[u.status]}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        {u.roles.length === 0 && <span className="text-ink-400 text-xs">—</span>}
                        {u.roles.map((r) => (
                          <Badge key={r} tone="brand">
                            {r}
                          </Badge>
                        ))}
                      </div>
                    </TD>
                    <TD>
                      {u.twoFactorEnabled ? (
                        <ShieldCheck size={16} className="text-success-500" aria-label="Ativado" />
                      ) : (
                        <CircleX size={16} className="text-ink-400" aria-label="Desativado" />
                      )}
                    </TD>
                    <TD>
                      {u.emailVerified ? (
                        <CheckCircle2 size={16} className="text-success-500" />
                      ) : (
                        <CircleX size={16} className="text-warning-500" />
                      )}
                    </TD>
                    <TD className="text-ink-500 text-right text-xs">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('pt-BR') : 'nunca'}
                    </TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditing(u)}
                          aria-label="Editar"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Resetar senha"
                          disabled={resetPwd.isPending}
                          onClick={() => {
                            const pw = window.prompt(
                              `Nova senha para ${u.email} (mínimo 8 caracteres):`,
                            );
                            if (pw === null) return;
                            if (pw.length < 8) {
                              alert('Senha muito curta (mínimo 8 caracteres).');
                              return;
                            }
                            resetPwd.mutate({ id: u.id, newPassword: pw });
                          }}
                        >
                          <KeyRound size={14} className="text-brand-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Excluir"
                          disabled={u.id === me?.id || deleteMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir ${u.email}?`)) {
                              deleteMutation.mutate(u.id);
                            }
                          }}
                        >
                          <Trash2 size={14} className="text-danger-500" />
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pagination
              page={page}
              pageSize={usersQuery.data.pageSize}
              total={usersQuery.data.total}
              onChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            title="Nenhum usuário encontrado"
            description={q ? 'Limpe a busca e tente novamente.' : 'Crie o primeiro usuário admin.'}
            action={<Button onClick={() => setCreating(true)}>Novo usuário</Button>}
          />
        )}
      </Card>

      {creating && rolesQuery.data && (
        <UserDrawer
          mode="create"
          roles={rolesQuery.data}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {editing && rolesQuery.data && (
        <UserDrawer
          mode="edit"
          user={editing}
          roles={rolesQuery.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
    </div>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="border-ink-200 bg-ink-50/50 text-ink-500 flex items-center justify-between border-t px-4 py-3 text-xs">
      <span>
        Página {page} de {totalPages} · {total} no total
      </span>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
        >
          Anterior
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
        >
          Próxima
        </Button>
      </div>
    </div>
  );
}

function UserDrawer({
  mode,
  user,
  roles,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  user?: UserListItem;
  roles: { key: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(user?.email ?? '');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [password, setPassword] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(user?.roles ?? []);
  const [status, setStatus] = useState<UserListItem['status']>(user?.status ?? 'ACTIVE');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        const input: CreateUserInput = {
          email,
          fullName,
          password,
          roles: selectedRoles,
          emailVerified: true,
        };
        return api.createUser(input);
      }
      const input: UpdateUserInput = {
        fullName,
        status,
        roles: selectedRoles,
      };
      return api.updateUser(user!.id, input);
    },
    onSuccess: () => onSaved(),
    onError: (err) => {
      if (err instanceof PnlApiError) {
        setError(
          err.api.fields ? Object.values(err.api.fields).flat().join(' · ') : err.api.message,
        );
      } else {
        setError('Erro de conexão.');
      }
    },
  });

  function toggleRole(key: string) {
    setSelectedRoles((curr) =>
      curr.includes(key) ? curr.filter((r) => r !== key) : [...curr, key],
    );
  }

  return (
    <div
      className="bg-ink-900/40 animate-fade-in fixed inset-0 z-50 flex justify-end backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="shadow-lift h-full w-full max-w-md overflow-y-auto bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-200 border-b px-6 py-4">
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            {mode === 'create' ? 'Novo usuário' : 'Editar usuário'}
          </p>
          <h2 className="font-display text-ink-900 mt-1 text-xl font-semibold">
            {mode === 'create' ? 'Criar conta admin' : user?.email}
          </h2>
        </div>

        <form
          className="space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}

          {mode === 'create' && (
            <Field label="Email" required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </Field>
          )}

          <Field label="Nome completo" required>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </Field>

          {mode === 'create' && (
            <Field
              label="Senha temporária"
              required
              hint="Mínimo 8 caracteres com maiúscula, minúscula e número."
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </Field>
          )}

          {mode === 'edit' && (
            <Field label="Status">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as UserListItem['status'])}
                className="form-select border-ink-200 text-ink-900 shadow-soft focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4"
              >
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
                <option value="BLOCKED">Bloqueado</option>
                <option value="PENDING_VERIFICATION">Pendente verificação</option>
              </select>
            </Field>
          )}

          <Field label="Roles" hint="Selecione um ou mais.">
            <div className="border-ink-200 shadow-soft space-y-1.5 rounded-lg border bg-white p-3">
              {roles.map((r) => (
                <label
                  key={r.key}
                  className="hover:bg-ink-50 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox border-ink-300 text-brand-500 focus:ring-brand-500/30 h-4 w-4 rounded"
                    checked={selectedRoles.includes(r.key)}
                    onChange={() => toggleRole(r.key)}
                  />
                  <span className="text-ink-900 font-medium">{r.name}</span>
                  <span className="text-2xs text-ink-500 ml-auto font-mono">{r.key}</span>
                </label>
              ))}
            </div>
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {mode === 'create' ? 'Criar usuário' : 'Salvar alterações'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
